import { createContext } from 'react';

import type { SnackbarPlacement } from '../Snackbar/index.js';
import { Spacing } from '../typings/index.ts';

/**
 * Will show the alert as a grey banner
 */
export const MUTED_ALERT = 'muted';
/**
 * Will show the alert as a blue banner
 */
export const INFO_ALERT = 'info';
/**
 * Will show the alert as a orange banner
 */
export const WARNING_ALERT = 'warning';
/**
 * Will show the alert as a red banner
 */
export const CRITICAL_ALERT = 'critical';
/**
 * Will show the alert as a snackbar
 */
export const TEMPORARY_ALERT = 'temporary';
/**
 * Will delete the alert with the same id if it exists
 */
export const CLEAR_ALERT = 'CLEAR_ALERT';

export const ALERT_TYPES = {
  /**
   * Will show the alert as a grey banner
   */
  MUTED: MUTED_ALERT,
  /**
   * Will show the alert as a blue banner
   */
  INFO: INFO_ALERT,
  /**
   * Will show the alert as a orange banner
   */
  WARNING: WARNING_ALERT,
  /**
   * Will show the alert as a red banner
   */
  CRITICAL: CRITICAL_ALERT,
  /**
   * Will show the alert as a snackbar
   */
  TEMPORARY: TEMPORARY_ALERT,
  /**
   * Will delete the alert with the same id if it exists
   */
  CLEAR: CLEAR_ALERT
} as const;

/**
 * Call to clear the alert immediately
 */
type ClearSnackbarAlert = () => void;

export interface SnackbarAction {
  /**
   * Label of an action for a snackbar action
   */
  label: string;
  /**
   * Fired onClick of a snackbars action
   */
  onClick: (clearAlert: ClearSnackbarAlert) => void;
}

export interface Alert {
  /**
   * Type of alert (mostly just color)
   */
  type: typeof MUTED_ALERT | typeof INFO_ALERT | typeof WARNING_ALERT | typeof CRITICAL_ALERT;
  /**
   * Unique id of the alert
   */
  id: string;
  /**
   * Message to be displayed in the alert
   */
  message: string;
  /**
   * Fired on dismiss of an alert
   */
  onDismiss?: () => void;
  /**
   * Whether to allow dismissing the alert
   */
  isDismissable?: boolean;
}
export interface SnackbarAlert extends Omit<Alert, 'type' | 'isDismissable'> {
  type: typeof TEMPORARY_ALERT;
  /**
   * Optional button to add to a snackbar
   */
  action?: SnackbarAction;
  /**
   * Where to place the snackbar on screen
   * @default ['bottom', 'center']
   */
  placement?: SnackbarPlacement;
  /**
   * An `ms` expression of how long the snackbar should be shown for
   * @default `7.5s`
   */
  timeout?: string;
  /**
   * Spacing around the snackbar
   */
  spacing?: Spacing;
}

export interface ClearAlert {
  type: typeof CLEAR_ALERT;
  /**
   * The id of the specific alert to clear
   * __Note:__ If no id is provided, all alerts will be cleared
   */
  id?: string;
}

/**
 * Used to dispatch an alert, snackbar or clear an existing alert or snackbar
 */
export type DispatchAlert = (alert: Alert | SnackbarAlert | ClearAlert) => void;
const NO_ALERTS: (Alert | SnackbarAlert)[] = [];
/**
 * Logic for deciding what alerts to currently display
 * @param currentAlerts The list of currently displayed alerts
 * @param nextAlert The alert that was just dispatched
 */
export function reduceAlerts(
  currentAlerts: (Alert | SnackbarAlert)[],
  nextAlert: SnackbarAlert | Alert | ClearAlert
): (Alert | SnackbarAlert)[] {
  const filteredAlerts = currentAlerts.filter(({ id }) => id !== nextAlert.id);
  if (nextAlert.type === CLEAR_ALERT) {
    if (!nextAlert.id) {
      return NO_ALERTS;
    }
    if (filteredAlerts.length === currentAlerts.length) {
      return currentAlerts;
    }
    return filteredAlerts;
  }

  return filteredAlerts.concat(nextAlert);
}

export interface AlertContext {
  /**
   * The list of currently displayed alerts
   */
  alerts: Alert[];
  /**
   * The list of currently displayed snackbars
   */
  snackbars: SnackbarAlert[];
  /**
   * Dispatches an alert action, adding/removing to the queue of alerts to display
   * @deprecated use `useDispatchAlert` instead
   */
  dispatchAlert: DispatchAlert;
}

export default createContext({
  alerts: [],
  snackbars: [],
  dispatchAlert: () => 0
} as AlertContext);
